// Analytics Core - Combined AnalyticsService and SessionTracker
// This file combines the two main classes to avoid circular dependencies

import { GA4Provider } from './GA4Provider';
import { PrivacyManager } from './PrivacyManager';
import { EventProcessor } from './EventProcessor';
import { AnalyticsLogger } from './AnalyticsLogger';
import { AnalyticsHealthMonitor } from './AnalyticsHealthMonitor';
import { getSessionId, getSessionStartTime, clearSessionData, getDeviceType, calculateEngagementScore, type EngagementFactors } from './utils';
import { GA4_EVENT_NAMES, DEFAULT_CONFIG } from './constants';
import type {
  AnalyticsConfig,
  SessionStartData,
  SessionEndData,
  InstrumentSelectionData,
  InstrumentChangeData,
  PresetApplicationData,
  ParameterAdjustmentData,
  ParameterChangeCompleteData,
  CustomParametersToggleData,
  GridCellToggleData,
  NotePitchChangeData,
  GridDragOperationData,
  RowActionData,
  PlaybackStartData,
  PlaybackStopData,
  BPMChangeData,
  CompositionExportData,
  CompositionSaveLoadData,
  AIGenerationStartData,
  AIGenerationCompleteData,
  AIGenerationErrorData,
  VolumeAdjustmentData,
  MuteToggleData,
  TemplateLoadData,
  TemplateSaveData,
  EventData,
} from './types';

/**
 * AnalyticsService class
 * Main service for tracking user analytics events
 */
export class AnalyticsService {
  private ga4Provider: GA4Provider;
  private privacyManager: PrivacyManager;
  private eventProcessor: EventProcessor;
  private logger: AnalyticsLogger;
  private healthMonitor: AnalyticsHealthMonitor;
  private isInitialized: boolean = false;

  constructor() {
    this.ga4Provider = new GA4Provider();
    this.privacyManager = new PrivacyManager();
    this.logger = new AnalyticsLogger();
    this.healthMonitor = new AnalyticsHealthMonitor();
    
    // Create event processor with send callback
    this.eventProcessor = new EventProcessor((eventName: string, eventData: EventData) => {
      this.sendEvent(eventName, eventData);
    });
  }

  /**
   * Initialize analytics service
   */
  async initialize(config: AnalyticsConfig): Promise<void> {
    try {
      this.logger.setDebugMode(config.debugMode || false);
      this.logger.logInfo('Initializing analytics service', config);

      // Check privacy settings
      if (!config.enabled) {
        this.logger.logInfo('Analytics disabled by configuration');
        this.healthMonitor.setInitializationStatus('failed');
        return;
      }

      if (!this.privacyManager.isTrackingAllowed()) {
        this.logger.logInfo('Analytics blocked by privacy settings');
        this.healthMonitor.setInitializationStatus('failed');
        return;
      }

      // Initialize GA4
      await this.ga4Provider.initialize(config.trackingId, config.debugMode || false);

      this.isInitialized = true;
      this.healthMonitor.setInitializationStatus('success');
      this.logger.logInfo('Analytics service initialized successfully');
    } catch (error) {
      this.logger.logError(error as Error, 'Failed to initialize analytics');
      this.healthMonitor.setInitializationStatus('failed');
      throw error;
    }
  }

  /**
   * Send event to analytics provider
   */
  private sendEvent(eventName: string, eventData: EventData): void {
    if (!this.isInitialized) {
      this.logger.logWarning('Analytics not initialized, skipping event');
      return;
    }

    if (!this.privacyManager.isTrackingAllowed()) {
      this.logger.logWarning('Event blocked by privacy settings');
      return;
    }

    try {
      // Sanitize event data
      const sanitizedData = this.privacyManager.sanitizeEventData(eventData);

      // Send to GA4
      this.ga4Provider.sendEvent(eventName, sanitizedData);
      
      // Log event
      this.logger.logEvent(eventName, sanitizedData);
      
      // Update health metrics
      this.healthMonitor.recordEventSent();
    } catch (error) {
      this.logger.logError(error as Error, `Failed to send event: ${eventName}`);
      this.healthMonitor.recordEventFailed();
    }
  }

  // Session Tracking
  trackSessionStart(data: SessionStartData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.SESSION_START, data, { priority: 'high' });
  }

  trackSessionEnd(data: SessionEndData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.SESSION_END, data, { priority: 'high' });
    this.eventProcessor.flush();
  }

  // Instrument Tracking
  trackInstrumentSelection(data: InstrumentSelectionData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.INSTRUMENT_SELECT, data);
  }

  trackInstrumentChange(data: InstrumentChangeData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.INSTRUMENT_CHANGE, data);
  }

  trackPresetApplication(data: PresetApplicationData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.PRESET_APPLY, data);
  }

  // Parameter Tracking
  trackParameterAdjustment(data: ParameterAdjustmentData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.PARAMETER_ADJUST, data, {
      debounceMs: DEFAULT_CONFIG.DEBOUNCE_MS,
    });
  }

  trackParameterChangeComplete(data: ParameterChangeCompleteData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.PARAMETER_COMPLETE, data);
  }

  trackCustomParametersToggle(data: CustomParametersToggleData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.CUSTOM_PARAMS_TOGGLE, data);
  }

  // Grid Tracking
  trackGridCellToggle(data: GridCellToggleData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.GRID_CELL_TOGGLE, data);
  }

  trackNotePitchChange(data: NotePitchChangeData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.NOTE_PITCH_CHANGE, data);
  }

  trackGridDragOperation(data: GridDragOperationData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.GRID_DRAG, data, { batch: true });
  }

  trackRowAction(data: RowActionData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.ROW_ACTION, data);
  }

  // Playback Tracking
  trackPlaybackStart(data: PlaybackStartData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.PLAYBACK_START, data);
  }

  trackPlaybackStop(data: PlaybackStopData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.PLAYBACK_STOP, data);
  }

  trackBPMChange(data: BPMChangeData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.BPM_CHANGE, data, {
      debounceMs: DEFAULT_CONFIG.DEBOUNCE_MS,
    });
  }

  // Composition Tracking
  trackCompositionExport(data: CompositionExportData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.COMPOSITION_EXPORT, data);
  }

  trackCompositionSaveLoad(data: CompositionSaveLoadData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.COMPOSITION_SAVE_LOAD, data);
  }

  // AI Generation Tracking
  trackAIGenerationStart(data: AIGenerationStartData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.AI_GENERATION_START, data);
  }

  trackAIGenerationComplete(data: AIGenerationCompleteData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.AI_GENERATION_COMPLETE, data);
  }

  trackAIGenerationError(data: AIGenerationErrorData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.AI_GENERATION_ERROR, data, { priority: 'high' });
  }

  // Mixing Tracking
  trackVolumeAdjustment(data: VolumeAdjustmentData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.VOLUME_ADJUST, data, {
      debounceMs: DEFAULT_CONFIG.DEBOUNCE_MS,
    });
  }

  trackMuteToggle(data: MuteToggleData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.MUTE_TOGGLE, data);
  }

  // Template Tracking
  trackTemplateLoad(data: TemplateLoadData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.TEMPLATE_LOAD, data);
  }

  trackTemplateSave(data: TemplateSaveData): void {
    this.eventProcessor.processEvent(GA4_EVENT_NAMES.TEMPLATE_SAVE, data);
  }

  // Privacy Controls
  setOptOut(optOut: boolean): void {
    this.privacyManager.setOptOut(optOut);
    if (optOut) {
      this.eventProcessor.clear();
    }
  }

  getOptOutStatus(): boolean {
    return this.privacyManager.getOptOut();
  }

  // Health Monitoring
  getHealthMetrics() {
    return this.healthMonitor.getHealthStatus();
  }
}

/**
 * Session activity tracking
 */
interface SessionActivity {
  gridEditing: number;
  parameterAdjustment: number;
  playback: number;
  aiGeneration: number;
  mixing: number;
  templateUsage: number;
}

/**
 * Session metrics for tracking
 */
interface SessionMetrics {
  eventCount: number;
  activities: SessionActivity;
  instrumentsUsed: Set<string>;
  parametersAdjusted: number;
  cellsActivated: number;
  playbackIterations: number;
  aiGenerationsUsed: number;
  templatesUsed: number;
}

/**
 * SessionTracker class
 * Manages session lifecycle and metrics tracking
 */
export class SessionTracker {
  private sessionId: string;
  private sessionStartTime: number;
  private metrics: SessionMetrics;
  private timeoutId: NodeJS.Timeout | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private analyticsService: AnalyticsService | null = null;

  constructor() {
    this.sessionId = getSessionId();
    this.sessionStartTime = getSessionStartTime();
    this.metrics = {
      eventCount: 0,
      activities: {
        gridEditing: 0,
        parameterAdjustment: 0,
        playback: 0,
        aiGeneration: 0,
        mixing: 0,
        templateUsage: 0,
      },
      instrumentsUsed: new Set(),
      parametersAdjusted: 0,
      cellsActivated: 0,
      playbackIterations: 0,
      aiGenerationsUsed: 0,
      templatesUsed: 0,
    };
  }

  /**
   * Set analytics service reference (called after both are created)
   */
  setAnalyticsService(service: AnalyticsService): void {
    this.analyticsService = service;
  }

  /**
   * Start session tracking
   */
  startSession(userType: 'authenticated' | 'anonymous' = 'anonymous', userId?: string): void {
    if (!this.analyticsService) {
      console.warn('[SessionTracker] Analytics service not set');
      return;
    }

    // Track session start
    const sessionStartData: SessionStartData = {
      userType,
      referrerSource: document.referrer || 'direct',
      deviceType: getDeviceType(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timestamp: Date.now(),
      ...(userId && { userId }), // Only include userId if defined
    };

    this.analyticsService.trackSessionStart(sessionStartData);

    // Set up session timeout
    this.resetTimeout();

    // Set up visibility change handler
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.endSession();
      } else {
        this.resetTimeout();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Set up beforeunload handler
    this.beforeUnloadHandler = () => {
      this.endSession();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /**
   * End session tracking
   */
  endSession(): void {
    if (!this.analyticsService) {
      return;
    }

    const sessionDuration = Date.now() - this.sessionStartTime;
    const sessionDurationMinutes = sessionDuration / (1000 * 60);

    // Calculate engagement score
    const engagementFactors: EngagementFactors = {
      instrumentsUsed: this.metrics.instrumentsUsed.size,
      parametersAdjusted: this.metrics.parametersAdjusted,
      cellsActivated: this.metrics.cellsActivated,
      playbackIterations: this.metrics.playbackIterations,
      sessionDuration: sessionDurationMinutes,
      aiGenerationsUsed: this.metrics.aiGenerationsUsed,
      templatesUsed: this.metrics.templatesUsed,
    };

    const engagementScore = calculateEngagementScore(engagementFactors);
    const primaryActivities = this.getPrimaryActivities();
    const featureTimeSpent = this.calculateFeatureTimeSpent(sessionDuration);

    this.analyticsService.trackSessionEnd({
      sessionId: this.sessionId,
      sessionDuration,
      totalEvents: this.metrics.eventCount,
      primaryActivities,
      featureTimeSpent,
      engagementScore,
      timestamp: Date.now(),
    });

    clearSessionData();
    this.cleanup();
  }

  /**
   * Record activity to reset timeout
   */
  recordActivity(activityType?: keyof SessionActivity): void {
    this.metrics.eventCount++;

    if (activityType) {
      this.metrics.activities[activityType]++;
    }

    this.resetTimeout();
  }

  /**
   * Update session metrics
   */
  updateMetrics(updates: Partial<Omit<SessionMetrics, 'eventCount' | 'activities'>>): void {
    if (updates.instrumentsUsed) {
      updates.instrumentsUsed.forEach(instrument => {
        this.metrics.instrumentsUsed.add(instrument);
      });
    }
    if (updates.parametersAdjusted !== undefined) {
      this.metrics.parametersAdjusted += updates.parametersAdjusted;
    }
    if (updates.cellsActivated !== undefined) {
      this.metrics.cellsActivated += updates.cellsActivated;
    }
    if (updates.playbackIterations !== undefined) {
      this.metrics.playbackIterations += updates.playbackIterations;
    }
    if (updates.aiGenerationsUsed !== undefined) {
      this.metrics.aiGenerationsUsed += updates.aiGenerationsUsed;
    }
    if (updates.templatesUsed !== undefined) {
      this.metrics.templatesUsed += updates.templatesUsed;
    }
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Reset session timeout
   */
  private resetTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.endSession();
    }, DEFAULT_CONFIG.SESSION_TIMEOUT_MS);
  }

  /**
   * Get primary activities based on activity counts
   */
  private getPrimaryActivities(): string[] {
    const activities = Object.entries(this.metrics.activities)
      .filter(([_, count]) => count > 0)
      .sort(([_, a], [__, b]) => b - a)
      .slice(0, 3)
      .map(([activity]) => activity);

    return activities;
  }

  /**
   * Calculate approximate time spent on each feature
   */
  private calculateFeatureTimeSpent(totalDuration: number): Record<string, number> {
    const totalActivityCount = Object.values(this.metrics.activities).reduce((sum, count) => sum + count, 0);

    if (totalActivityCount === 0) {
      return {};
    }

    const timeSpent: Record<string, number> = {};

    for (const [activity, count] of Object.entries(this.metrics.activities)) {
      if (count > 0) {
        timeSpent[activity] = Math.round((count / totalActivityCount) * totalDuration);
      }
    }

    return timeSpent;
  }

  /**
   * Clean up event listeners and timers
   */
  private cleanup(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}

// Create and export singletons
export const analyticsService = new AnalyticsService();
export const sessionTracker = new SessionTracker();

// Set up the relationship
sessionTracker.setAnalyticsService(analyticsService);
